import { IProductTitle, TClientProduct, TEmptyProduct, TNamesplit, TProductKeywordsUploadParams } from 'shared/types';
import { filtreAndOrderColumns } from 'shared/table';
import { cdmApi } from './index';

const PREFIX = '/client-products';

const defaultRetailerAndLang = { retailer: 'default', lang: 'default', isBrut: false, isAliases: true };

const getNbSplits = (p: TClientProduct) => {
    let lastIndex = 0;
    p.name_split.splits.forEach((s, idx) => {
        if (s.val !== '') lastIndex = idx;
    });

    return lastIndex;
};

export type TGetAllClientProductsQueryProps = {
    retailer?: string;
    lang?: string;
    isBrut?: boolean;
    isAliases?: boolean;
    omitLang?: boolean;
    isProductTitle?: boolean;
};

export type TGetAllClientProductsPayload = {
    code: number;
    message: string;
    products: TClientProduct[];
    aliases?: Record<string, string[]>;
    headersRow: string[];
    splitsTitles: string[];
    tagWeights: (number | null)[];
};

const productsApi = cdmApi.injectEndpoints({
    endpoints: (builder) => ({
        // CRUD routes
        getAllClientProducts: builder.query<TGetAllClientProductsPayload, TGetAllClientProductsQueryProps>({
            query: (params) => ({ url: `${PREFIX}/`, method: 'get', params }),
            providesTags: ['Product'],
        }),
        getListKey: builder.query<
            { code: number; message: string; list: string[] },
            { retailer: string; lang: string; isRetailerAndLang: string; key: string; companyId: number }
        >({ query: (params) => ({ url: `${PREFIX}/list-key`, method: 'get', params }) }),
        getFacByRetailer: builder.query<{ code: number; data: Record<string, number[]>; tag_split: string[] }, void>({
            query: () => ({ url: `${PREFIX}/fac`, method: 'get' }),
            providesTags: ['Facs'],
        }),
        updateClientProductV2: builder.mutation<
            { code: number; result: TClientProduct },
            {
                product: IProductTitle | TEmptyProduct;
                isProductTitle: boolean;
                retailer?: string;
                lang?: string;
                isBrut?: boolean;
                isAliases?: boolean;
                headers?: string[];
                omitLang?: boolean;
            }
        >({
            query: (data) => ({ url: `${PREFIX}/`, method: 'put', data }),
            invalidatesTags: ['Product'],
            async onQueryStarted(
                { retailer, lang, isAliases, isBrut, isProductTitle, omitLang },
                { dispatch, queryFulfilled },
            ) {
                const {
                    data: { result },
                } = await queryFulfilled;
                let patchResult;
                if (isProductTitle) {
                    const resultNameSplits = result.name_split as any as Record<string, Record<string, TNamesplit>>;
                    const newProductName = (result.name as any)?.[retailer || 'default']?.[lang || 'default'];
                    const newNameSplits = resultNameSplits?.[retailer || 'default']?.[lang || 'default'] || {
                        fac: [],
                        splits: [],
                    };

                    const tempProduct: Record<string, any> = {
                        ...result,
                        name_split: newNameSplits,
                    };

                    patchResult = dispatch(
                        productsApi.util.updateQueryData(
                            'getAllClientProducts',
                            {
                                retailer: retailer !== undefined ? retailer : 'default',
                                lang: lang !== undefined ? lang : 'default',
                                isBrut: isBrut !== undefined ? isBrut : false,
                                isAliases: isAliases !== undefined ? isAliases : true,
                            },
                            (draft) => {
                                const draftRef = draft;
                                const indexElem = draftRef.products.findIndex((el) => el.ean === tempProduct.ean);

                                draftRef.products[indexElem] = {
                                    ...tempProduct,
                                    name: newProductName || draftRef.products[indexElem].name || '',
                                } as TClientProduct;
                            },
                        ),
                    );

                    queryFulfilled.catch(patchResult.undo);
                } else {
                    patchResult = dispatch(
                        productsApi.util.updateQueryData(
                            'getAllClientProducts',
                            {
                                retailer: retailer || 'default',
                                lang: lang || 'default',
                                isBrut: isBrut || false,
                                isAliases: isAliases || true,
                                omitLang: omitLang || false,
                            },
                            (draft) => {
                                const draftRef = draft;
                                const indexElem = draftRef.products.findIndex((el) => el.ean === result.ean);
                                draftRef.products[indexElem] = result;
                            },
                        ),
                    );

                    queryFulfilled.catch(patchResult.undo);
                }
            },
        }),
        addClientProductV2: builder.mutation<
            { code: number; created: TClientProduct; headers: string[] },
            {
                product: IProductTitle | TEmptyProduct;
                isProductTitle: boolean;
                isAliases?: boolean;
                isBrut?: boolean;
                retailer?: string;
                lang?: string;
                omitLang?: boolean;
            }
        >({
            query: (data) => ({ url: `${PREFIX}/`, method: 'post', data }),
            invalidatesTags: ['Product'],
            async onQueryStarted({ retailer, lang, isAliases, isBrut, omitLang }, { dispatch, queryFulfilled }) {
                const {
                    data: { created, headers },
                } = await queryFulfilled;

                const patchResult = dispatch(
                    productsApi.util.updateQueryData(
                        'getAllClientProducts',
                        {
                            retailer: retailer || 'default',
                            lang: lang || 'default',
                            isBrut: isBrut || false,
                            isAliases: isAliases || true,
                            omitLang: omitLang || false,
                        },
                        (draft) => {
                            const draftRef = draft;
                            draftRef.products.push(created);
                            const newHeaders = new Set(draftRef.headersRow.concat(headers));
                            draftRef.headersRow = filtreAndOrderColumns(Array.from(newHeaders));
                        },
                    ),
                );

                queryFulfilled.catch(patchResult.undo);
            },
        }),
        // added other params only to delete base products from get
        deleteClientProductV2: builder.mutation<
            { code: number; deleted: number; updated: number },
            {
                products: string[];
                retailer?: string;
                lang?: string;
                isBrut?: boolean;
                isAliases?: boolean;
                isProductTitle?: boolean;
                omitLang?: boolean;
            }
        >({
            query: (data) => ({ url: `${PREFIX}/`, method: 'delete', data }),
            invalidatesTags: ['Product'],
            async onQueryStarted(
                { products, retailer, lang, isAliases, isBrut, omitLang },
                { dispatch, queryFulfilled },
            ) {
                const {
                    data: { deleted, updated },
                } = await queryFulfilled;

                if (deleted > 0 || updated > 0) {
                    const patchResult = dispatch(
                        productsApi.util.updateQueryData(
                            'getAllClientProducts',
                            {
                                retailer: retailer || 'default',
                                lang: lang || 'default',
                                isBrut: isBrut || false,
                                isAliases: isAliases || true,
                                omitLang: omitLang || false,
                            },
                            (draft) => {
                                const draftRef = draft;
                                draftRef.products = draftRef.products.filter((p) => !products.includes(p.ean));
                            },
                        ),
                    );

                    queryFulfilled.catch(patchResult.undo);
                }
            },
        }),

        exportClientProducts: builder.query<any, TProductKeywordsUploadParams>({
            query: (params) => ({ url: `${PREFIX}/export`, method: 'get', params, responseType: 'arraybuffer' }),
        }),
        uploadClientProducts: builder.mutation<{ code: number; data: string[] }, FormData>({
            query: (data) => ({ url: `${PREFIX}/upload`, method: 'post', data }),
            invalidatesTags: ['Product'],
        }),
        updateFacultativeNameSplits: builder.mutation<
            { code: number },
            { retailer: string; lang: string; fac: number[] }
        >({
            query: (data) => ({ url: `${PREFIX}/fac`, method: 'put', data }),
            invalidatesTags: ['Facs'],
        }),
        updateTagSplit: builder.mutation<
            { code: number; tags: string[]; tagWeights: (number | null)[] },
            { tags: string[]; tagWeights: (number | null)[] }
        >({
            query: (data) => ({ url: `${PREFIX}/tag-split`, method: 'put', data }),
            async onQueryStarted(data, { dispatch, queryFulfilled, getState }) {
                const resp = await queryFulfilled;

                if (resp?.data.code !== 200) {
                    return;
                }

                const cache = cdmApi.util.selectInvalidatedBy(getState(), ['Product'])[0];

                const patchResult = dispatch(
                    productsApi.util.updateQueryData('getAllClientProducts', cache.originalArgs, (draft) => {
                        const draftRef = draft;
                        draftRef.splitsTitles =
                            draftRef.splitsTitles?.map((tag, idx) => data.tags[idx] || `name_split_${idx + 1}`) || [];
                    }),
                );

                queryFulfilled.catch((e) => patchResult.undo());
            },
            invalidatesTags: ['Facs'],
        }),

        // Aliases routes
        getClientAliases: builder.query<{ code: number; aliases: Record<string, string[]> }, void>({
            query: () => ({ url: `${PREFIX}/aliases/`, method: 'get' }),
            providesTags: ['Aliases'],
        }),
        updateClientSingleAlias: builder.mutation<
            { code: number; updateResult: Record<string, number> },
            { key: string; aliases: string[] }
        >({
            query: (data) => ({ url: `${PREFIX}/aliases/`, method: 'PUT', data }),
            invalidatesTags: ['Product'],
            onQueryStarted(alias, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    productsApi.util.updateQueryData('getClientAliases', undefined, (draft) => {
                        const draftRef = draft;
                        draftRef.aliases[alias.key] = alias.aliases;
                    }),
                );

                queryFulfilled.catch(patchResult.undo);
            },
        }),
        deleteClientSingleAlias: builder.mutation<{ code: number; aliases: Record<string, string[]> }, { key: string }>(
            {
                query: (data) => ({ url: `${PREFIX}/aliases/`, method: 'DELETE', data }),
                invalidatesTags: ['Product'],
                onQueryStarted(alias, { dispatch, queryFulfilled }) {
                    const patchResult = dispatch(
                        productsApi.util.updateQueryData('getClientAliases', undefined, (draft) => {
                            const draftRef = draft;
                            draftRef.aliases[alias.key] = [];
                        }),
                    );

                    queryFulfilled.catch(patchResult.undo);
                },
            },
        ),
        addNewAlias: builder.mutation<any, { key: string; aliases: string[] }>({
            query: (data) => ({ url: `${PREFIX}/aliases/`, method: 'POST', data }),
            invalidatesTags: ['Product'],
            onQueryStarted(alias, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    productsApi.util.updateQueryData('getClientAliases', undefined, (draft) => {
                        const draftRef = draft;
                        draftRef.aliases[alias.key] = alias.aliases;
                    }),
                );

                queryFulfilled.catch(patchResult.undo);
            },
        }),
        exportClientAliases: builder.query<any, { isTemplate: boolean }>({
            query: (params) => ({
                url: `${PREFIX}/aliases/export`,
                method: 'get',
                params,
                responseType: 'arraybuffer',
            }),
        }),
        uploadClientAliases: builder.mutation<any, FormData>({
            query: (data) => ({ url: `${PREFIX}/aliases/upload`, method: 'post', data }),
            invalidatesTags: ['Product', 'Aliases'],
        }),
        // added other params only to delete base products from get
        deleteCustomColumns: builder.mutation<
            { code: number; customColumnsDeleted: string[] },
            { customColumns: string[] }
        >({
            query: (data) => ({ url: `${PREFIX}/customColumns`, method: 'delete', data }),
            invalidatesTags: ['Product'],
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetListKeyQuery,
    useGetAllClientProductsQuery,
    useLazyGetAllClientProductsQuery,
    useLazyExportClientProductsQuery,
    useUploadClientProductsMutation,
    useUpdateClientProductV2Mutation,
    useAddClientProductV2Mutation,
    useDeleteClientProductV2Mutation,
    useUpdateFacultativeNameSplitsMutation,
    useDeleteCustomColumnsMutation,
    useUpdateTagSplitMutation,
    useGetFacByRetailerQuery,

    useLazyExportClientAliasesQuery,
    useUploadClientAliasesMutation,
    useGetClientAliasesQuery,
    useUpdateClientSingleAliasMutation,
    useDeleteClientSingleAliasMutation,
    useAddNewAliasMutation,
} = productsApi;
